import { type ReactNode } from 'react';

import { accessValue } from '../../../core/utils/accessors/access-value.js';
import type { TrendDirection } from '../../core/components/single-value-renderer/components/trend/types/trend-direction.js';
import type { Timeseries } from '../../core/types/timeseries.js';
import type { SingleValueGridAccessorsConfig } from '../types/single-value-grid.js';

type TrendData = {
  direction?: TrendDirection;
  value?: number;
  label?: string;
};

type SingleValueData = {
  label?: string;
  prefixIcon?: ReactNode;
  sparklineData?: Timeseries | Timeseries[];
  value?: number | string;
  trendData?: TrendData;
};

/**
 * Retrieves data from the provided object using accessors specified in the accessors' configuration.
 *
 * @param data - The object containing the data.
 * @param accessors - The configuration specifying the accessors.
 * @returns The accessed data object.
 */
export function getDataFromAccessors(
  data: Record<string, unknown>,
  accessors: SingleValueGridAccessorsConfig,
) {
  const accessedData: SingleValueData = {};

  if (accessors.labelAccessor) {
    accessedData.label = accessValue<string>(data, accessors.labelAccessor);
  }

  if (accessors.prefixIconAccessor) {
    accessedData.prefixIcon = accessValue<ReactNode>(
      data,
      accessors.prefixIconAccessor,
    );
  }

  if (accessors.value?.dataAccessor) {
    accessedData.value = accessValue<number | string>(
      data,
      accessors.value?.dataAccessor,
    );
  }

  if (accessors.trend) {
    accessedData.trendData = {};

    if (accessors.trend.directionAccessor) {
      accessedData.trendData.direction = accessValue<TrendDirection>(
        data,
        accessors.trend?.directionAccessor,
      );
    }

    if (accessors.trend.valueAccessor) {
      accessedData.trendData.value = accessValue<number>(
        data,
        accessors.trend?.valueAccessor,
      );
    }

    if (accessors.trend.labelAccessor) {
      accessedData.trendData.label = accessValue<string>(
        data,
        accessors.trend?.labelAccessor,
      );
    }
  }

  if (accessors.sparkline?.dataAccessor) {
    accessedData.sparklineData = accessValue<Timeseries | Timeseries[]>(
      data,
      accessors.sparkline?.dataAccessor,
    );
  }

  return accessedData;
}
